import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { KeyValuePipe } from '@angular/common';

/**
 * Permission item shape accepted by the checklist.
 * Matches the shape used by `PermissionsManagementService.permissions()`.
 */
export interface PermissionChecklistItem {
  id: string;
  description?: string | null;
}

/**
 * Shared permission checklist component — the single source of truth for
 * rendering a grouped checkbox list of permissions.
 *
 * Used by:
 * - `RoleEditComponent` (select which permissions a role grants)
 * - `ApiKeyEditComponent` (select which permissions a key may use)
 *
 * Inputs:
 * - `permissions` — the full catalog to render (grouped by resource prefix)
 * - `selectedIds` — the currently checked permission ids
 *
 * Outputs:
 * - `selectedIdsChange` — emitted whenever the user toggles a checkbox
 *
 * The component uses `ViewEncapsulation.None` and BEM-style class names
 * prefixed with `sc-permission-checklist__` so the host can control layout
 * via the surrounding accordion or container.
 */
@Component({
  selector: 'sc-permission-checklist',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [KeyValuePipe],
  host: {
    '[class.sc-permission-checklist]': 'true',
  },
  template: `
    <div class="sc-permission-checklist__groups">
      @for (resource of permissionsByResource() | keyvalue; track resource.key) {
        <div class="sc-permission-checklist__group">
          <h4 class="sc-permission-checklist__group-title">
            {{ resource.key }}
          </h4>
          <div class="sc-permission-checklist__list">
            @for (perm of resource.value; track perm.id) {
              <label class="sc-permission-checklist__item">
                <input
                  type="checkbox"
                  class="sc-permission-checklist__checkbox"
                  [checked]="isSelected(perm.id)"
                  (change)="toggle(perm.id)"
                />
                <span class="sc-permission-checklist__label">
                  <strong>{{ actionLabel(perm.id) }}</strong>
                  @if (perm.description) {
                    <span class="sc-permission-checklist__desc">{{ perm.description }}</span>
                  }
                </span>
              </label>
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .sc-permission-checklist {
        display: block;
      }

      .sc-permission-checklist__groups {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-lg);
      }

      .sc-permission-checklist__group {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .sc-permission-checklist__group-title {
        margin: 0;
        font-size: var(--font-size-sm);
        font-weight: 600;
        color: var(--text-color-primary);
        text-transform: capitalize;
        padding-bottom: var(--spacing-xs);
        border-bottom: 1px solid var(--border-color-secondary);
      }

      .sc-permission-checklist__list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs);
      }

      .sc-permission-checklist__item {
        display: flex;
        align-items: flex-start;
        gap: var(--spacing-sm);
        padding: var(--spacing-xs) var(--spacing-sm);
        border-radius: var(--border-radius-sm);
        cursor: pointer;
        transition: background-color var(--transition-duration-fast) var(--transition-timing);
      }

      .sc-permission-checklist__item:hover {
        background-color: var(--surface-color-hover);
      }

      .sc-permission-checklist__checkbox {
        width: 16px;
        height: 16px;
        margin-top: 2px;
        accent-color: var(--color-primary);
        cursor: pointer;
      }

      .sc-permission-checklist__label {
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: var(--font-size-sm);
      }

      .sc-permission-checklist__label strong {
        color: var(--text-color-primary);
        text-transform: capitalize;
      }

      .sc-permission-checklist__desc {
        font-size: var(--font-size-xs);
        color: var(--text-color-secondary);
      }
    `,
  ],
})
export class PermissionChecklistComponent {
  /** Full catalog of permissions to render. */
  readonly permissions = input.required<PermissionChecklistItem[]>();

  /** Currently checked permission ids. */
  readonly selectedIds = input<Set<string>>(new Set());

  /** Emitted with the new Set of selected ids whenever the user toggles a checkbox. */
  readonly selectedIdsChange = output<Set<string>>();

  /** Search filter (optional — set externally to filter the list). */
  readonly filter = input<string>('');

  /** Permissions grouped by resource prefix, optionally filtered. */
  readonly permissionsByResource = computed(() => {
    const q = this.filter().trim().toLowerCase();
    let perms = this.permissions();
    if (q) {
      perms = perms.filter(p => p.id.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q));
    }
    const grouped: Record<string, PermissionChecklistItem[]> = {};
    for (const perm of perms) {
      const [resource] = perm.id.split(':');
      if (!grouped[resource]) {
        grouped[resource] = [];
      }
      grouped[resource].push(perm);
    }
    return grouped;
  });

  /** Count of selected permissions. */
  readonly selectedCount = computed(() => this.selectedIds().size);

  protected isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  protected toggle(id: string): void {
    const current = new Set(this.selectedIds());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.selectedIdsChange.emit(current);
  }

  /**
   * Extracts the action part of a permission id (everything after the first `:`).
   */
  protected actionLabel(id: string): string {
    const colonIndex = id.indexOf(':');
    return colonIndex === -1 ? id : id.slice(colonIndex + 1);
  }
}
